import React, { useState, useRef, useEffect } from 'react';
import { Mic, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface VoiceVisualizerProps {
  onRecordStart?: () => void;
  onRecordStop?: () => void;
  onTranscript?: (text: string) => void;
  compact?: boolean;
  size?: 'sm' | 'md' | 'lg';
  isRecording?: boolean;
  isProcessing?: boolean;
  errorText?: string | null;
  onToggle?: () => void;
}

const GROQ_API_KEY = "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";

export const VoiceVisualizer: React.FC<VoiceVisualizerProps> = ({
  onRecordStart,
  onRecordStop,
  onTranscript,
  compact = false,
  size,
  isRecording: controlledIsRecording,
  isProcessing: controlledIsProcessing,
  errorText: controlledErrorText,
  onToggle
}) => {
  const [localIsRecording, localSetIsRecording] = useState(false);
  const [localIsProcessing, localSetIsProcessing] = useState(false);
  const [localErrorText, localSetErrorText] = useState<string | null>(null);

  const isRecording = controlledIsRecording !== undefined ? controlledIsRecording : localIsRecording;
  const isProcessing = controlledIsProcessing !== undefined ? controlledIsProcessing : localIsProcessing;
  const errorText = controlledErrorText !== undefined ? controlledErrorText : localErrorText;

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        localSetIsProcessing(true);
        localSetErrorText(null);

        try {
          const file = new File([audioBlob], 'audio.webm', { type: 'audio/webm' });
          const formData = new FormData();
          formData.append('file', file);
          formData.append('model', 'whisper-large-v3-turbo');
          formData.append('temperature', '0');
          formData.append('response_format', 'verbose_json');

          const response = await fetch('https://api.groq.com/openai/v1/audio/transcriptions', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${GROQ_API_KEY}`
            },
            body: formData
          });

          if (!response.ok) {
            throw new Error(`Transcription failed: ${response.statusText}`);
          }

          const data = await response.json();
          if (data.text && onTranscript) {
            onTranscript(data.text);
          }
        } catch (err: any) {
          console.error("Whisper error:", err);
          localSetErrorText("Transcription Error");
        } finally {
          localSetIsProcessing(false);
          // Stop all tracks
          stream.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start();
      localSetIsRecording(true);
      localSetErrorText(null);
      if (onRecordStart) onRecordStart();

    } catch (err) {
      console.error("Failed to access microphone:", err);
      localSetErrorText("Mic Access Denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      localSetIsRecording(false);
      if (onRecordStop) onRecordStop();
    }
  };

  const toggleRecording = () => {
    if (onToggle) {
      onToggle();
      return;
    }
    if (isProcessing) return;
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const finalSize = size || (compact ? 'md' : 'lg');

  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-16 h-16"
  }[finalSize];

  const maskInset = {
    sm: "inset-[1.5px]",
    md: "inset-[1.5px]",
    lg: "inset-[2.5px]"
  }[finalSize];

  const iconSize = {
    sm: "w-3.5 h-3.5 text-zinc-300",
    md: "w-4.5 h-4.5 text-zinc-300",
    lg: "w-6 h-6 text-zinc-300"
  }[finalSize];

  const wavesHeightRange = {
    sm: ['3px', '10px', '3px'],
    md: ['4px', '12px', '4px'],
    lg: ['6px', '22px', '6px']
  }[finalSize];

  const waveCount = finalSize === 'sm' ? 3 : finalSize === 'md' ? 3 : 5;

  return (
    <div className="relative flex flex-col items-center justify-center">
      <div className="relative flex items-center justify-center">
        <button
          onClick={toggleRecording}
          disabled={isProcessing}
          className={cn(
            "relative flex items-center justify-center transition-all duration-300 rounded-full overflow-hidden p-[1.5px] shadow-lg active:scale-95 border-none bg-transparent",
            sizeClasses,
            isRecording ? "shadow-[0_0_20px_rgba(239,68,68,0.5)]" : "hover:shadow-[0_0_15px_rgba(59,130,246,0.4)]"
          )}
        >
          {/* Rotating gradient background */}
          <div
            className={cn(
              "absolute inset-[-60%] rounded-full blur-[1px] opacity-90 animate-[slow-spin_8s_linear_infinite]",
              isRecording
                ? "bg-[conic-gradient(from_180deg,#ef4444,#ff6b6b,#dc2626,#ef4444)]"
                : isProcessing
                  ? "bg-[conic-gradient(from_180deg,#3b82f6,#60a5fa,#2563eb,#3b82f6)]"
                  : "bg-[conic-gradient(from_180deg,#3b82f6,#8b5cf6,#ec4899,#3b82f6)]"
            )}
          />
          {/* Inner mask */}
          <div className={cn("absolute rounded-full bg-[#1c1c1c] flex items-center justify-center", maskInset)} />

          {/* Content layer */}
          <div className="relative z-10 flex items-center justify-center w-full h-full text-zinc-300">
            {isProcessing ? (
              <Loader2 className={cn("animate-spin text-blue-400", iconSize)} />
            ) : isRecording ? (
              <div className="flex items-center justify-center gap-0.5">
                {[...Array(waveCount)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-0.5 bg-red-400 rounded-full"
                    animate={{ height: wavesHeightRange }}
                    transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut", delay: i * 0.1 }}
                  />
                ))}
              </div>
            ) : (
              <Mic className={cn("text-blue-400", iconSize)} />
            )}
          </div>
        </button>
      </div>

      {/* Futuristic status text */}
      {!compact && (
        <div className="absolute top-full mt-4 text-[10px] font-mono tracking-widest uppercase text-muted-foreground whitespace-nowrap">
          {errorText ? (
            <span className="text-destructive">{errorText}</span>
          ) : isRecording ? (
            <span className="text-destructive animate-pulse">Recording Uplink...</span>
          ) : isProcessing ? (
            <span className="text-primary animate-pulse">Processing...</span>
          ) : (
            ""
          )}
        </div>
      )}
    </div>
  );
};
